import { useCallback, useContext, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Snackbar,
    Typography
} from '@mui/material';
import { Close as CloseIcon, Visibility, VisibilityOff } from '@mui/icons-material';
// Our Components
import { AuthContext } from 'context/auth-context';
// Services
import { Users } from 'lib/api';
// Assets
import './FormContainer.css';

export const FormNewPassword = () => {
    const { businessInfo } = useContext(AuthContext);
    const [formValues, setFormValues] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({
        isOpen: false,
        position: {
            vertical: 'top',
            horizontal: 'center'
        }
    });

    const updateUserPassword = useCallback(async () => {
        setIsSubmitting(false);
        try {
            await Users.updatePasswordUser(formValues);

            resetForm();
            setSnackbar(prevValues => ({ ...prevValues, isOpen: true }));
        } catch (error) {
            setErrorMessage(error?.response?.data?.message);
        }
        setIsSubmitting(false);
    }, [formValues]);
    useEffect(() => {
        if (isSubmitting) {
            updateUserPassword();
        }
    }, [isSubmitting, updateUserPassword]);

    const resetForm = () => {
        setErrorMessage('');
        setFormValues({
            oldPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
    };

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') return;

        setSnackbar(prevValues => ({ ...prevValues, isOpen: false }));
    };

    const handleChange = e => {
        setFormValues({
            ...formValues,
            [e.target.name]: e.target.value
        });
    };

    const isFormValid = () => {
        const { newPassword, confirmPassword, oldPassword } = formValues;
        if (newPassword.length > 7 && newPassword === confirmPassword) {
            if (oldPassword.length > 7) return true;
        }
        return false;
    };

    return (
        <Box className="form-container">
            <Box display="flex" justifyContent="space-between">
                <Box>
                    <Box className="form-container_title">Manage Security Settings</Box>
                    <Box className="form-container_subtitle">Your new password must be at least 8 characters long</Box>
                </Box>
                <Box>
                    {isSubmitting ? (
                        <CircularProgress />
                    ) : (
                        <Button
                            variant="contained"
                            sx={{ borderRadius: '4px' }}
                            disabled={!isFormValid() || businessInfo?.is_frozen}
                            onClick={() => setIsSubmitting(true)}>
                            Save
                        </Button>
                    )}
                </Box>
            </Box>
            <Box display="flex" flexDirection="column" width="400px">
                <FormControl sx={{ marginTop: '20px' }}>
                    <Typography variant="subtitle2">Old Password</Typography>
                    <OutlinedInput
                        name="oldPassword"
                        placeholder="Enter old password"
                        onChange={handleChange}
                        required
                        value={formValues.oldPassword}
                        type={showOldPassword ? 'text' : 'password'}
                        size="small"
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => setShowOldPassword(!showOldPassword)}
                                    edge="end">
                                    {!showOldPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                </FormControl>
                <FormControl sx={{ marginTop: '20px' }}>
                    <Typography variant="subtitle2">New Password</Typography>
                    <OutlinedInput
                        name="newPassword"
                        onChange={handleChange}
                        required
                        value={formValues.newPassword}
                        type={showNewPassword ? 'text' : 'password'}
                        size="small"
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    edge="end">
                                    {!showNewPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                </FormControl>
                <FormControl sx={{ marginTop: '20px' }}>
                    <Typography variant="subtitle2">Retype New Password</Typography>
                    <OutlinedInput
                        name="confirmPassword"
                        onChange={handleChange}
                        required
                        value={formValues.confirmPassword}
                        type={showNewPassword ? 'text' : 'password'}
                        size="small"
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    edge="end">
                                    {!showNewPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                </FormControl>
                {errorMessage && (
                    <Box component="label" color="#F65570" fontSize="11px" marginTop="4px">
                        {errorMessage}
                    </Box>
                )}
            </Box>
            <Snackbar
                anchorOrigin={snackbar.position}
                open={snackbar.isOpen}
                onClose={() => setSnackbar(prevValues => ({ ...prevValues, isOpen: false }))}
                autoHideDuration={4000}
                action={
                    <>
                        <Button color="secondary" size="small" onClick={handleClose}></Button>
                        <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </>
                }>
                <Alert
                    className="form-container_snackbar-alert"
                    onClose={() => setSnackbar(prevValues => ({ ...prevValues, isOpen: false }))}
                    severity="success">
                    Profile changed successfully
                </Alert>
            </Snackbar>
        </Box>
    );
};
